import React, { useState, useRef, useEffect } from "react";
import ReactCrop, { Crop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "reactstrap";
import { PulseLoader } from "react-spinners";

interface ImageCropperProps {
  imageFile: File;
  onCropComplete: (croppedFile: File) => void;
  onCancel?: () => void;
  aspect?: number;
}

const ImageCropper: React.FC<ImageCropperProps> = ({ imageFile, onCropComplete, onCancel, aspect = 1 }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!imageFile) return;

    let cancelled = false;

    const normalizeTo34Blur = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const ow = img.naturalWidth;
            const oh = img.naturalHeight;

            let cw = ow;
            let ch = Math.round(ow / aspect);
            if (ch < oh) {
              ch = oh;
              cw = Math.round(oh * aspect);
            }

            const canvas = document.createElement("canvas");
            canvas.width = cw;
            canvas.height = ch;
            const ctx = canvas.getContext("2d");
            if (!ctx) return reject("No context");

            const bgScale = Math.max(cw / ow, ch / oh);
            const bgW = ow * bgScale;
            const bgH = oh * bgScale;
            const bgX = (cw - bgW) / 2;
            const bgY = (ch - bgH) / 2;

            ctx.filter = "blur(20px) brightness(1)";
            ctx.drawImage(img, bgX, bgY, bgW, bgH);

            ctx.filter = "none";

            const fgScale = Math.min(1, cw / ow, ch / oh);
            const fgW = ow * fgScale;
            const fgH = oh * fgScale;
            const fgX = (cw - fgW) / 2;
            const fgY = (ch - fgH) / 2;

            ctx.drawImage(img, fgX, fgY, fgW, fgH);

            resolve(canvas.toDataURL("image/jpeg", 0.92));
          };
          img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
      });
    };

    normalizeTo34Blur(imageFile).then((data) => {
      if (!cancelled) setSelectedImage(data);
    });

    return () => {
      cancelled = true;
    };
  }, [imageFile, aspect]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, aspect, width, height),
      width,
      height
    );
    setCrop(initial);
  };

  const getCroppedImage = async () => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement("canvas");
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

  
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const timestamp = new Date().getTime();
        const originalName = imageFile.name.split('.')[0];
        const fileName = `${originalName}_cropped_${timestamp}.jpeg`;
        const file = new File([blob], fileName, { type: "image/jpeg" });
        onCropComplete(file);
      }
    }, "image/jpeg");
  };

  return (
    <div className="cropper-modal-overlay">
      <div className="cropper-modal-card">
        <style>{`
          .cropper-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.7);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            overflow-y: auto;
            padding: 20px;
          }
          .cropper-modal-card {
            background-color: #fff;
            padding: 0;
            border-radius: 0;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
            border: 1px solid rgba(0, 0, 0, 0.15);
            width: fit-content;
            max-width: min(400px, 95%);
            overflow: hidden;
          }
          .cropper-preview-container {
            display: block;
            background-color: #f8f9fa;
            padding: 0;
          }
          .cropper-image {
            max-width: 100%;
            max-height: 55vh;
            display: block;
            margin: 0 auto;
          }
          .cropper-button-bar {
            padding: 15px 20px;
            background-color: #fff;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 10px;
            border-top: 1px solid #eee;
          }
          .cropper-btn-cancel {
            min-width: 120px;
            height: 40px;
            border-radius: 8px;
            font-weight: 500;
            font-size: 15px;
          }
          .cropper-btn-apply {
            min-width: 120px;
            height: 40px;
            border-radius: 8px;
            font-weight: 500;
            font-size: 15px;
          }
          .cropper-loader-container {
            padding: 40px;
            background-color: #fff;
            text-align: center;
          }
        `}</style>
      {selectedImage ? (
        <>
          <div className="cropper-preview-container">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            keepSelection
          >
            <img
              ref={imgRef}
              src={selectedImage}
              alt="Crop me"
                  className="cropper-image"
              onLoad={onImageLoad}
            />
          </ReactCrop>
            </div>

            <div className="cropper-button-bar">
              {onCancel && (
                <button
                  className="btn btn-outline-danger cropper-btn-cancel"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              )}
              <Button
                color="primary"
                onClick={getCroppedImage}
                className="cropper-btn-apply"
              >
            Apply Crop
          </Button>
            </div>
        </>
      ) : (
        <div className="cropper-loader-container">
          <PulseLoader color="#626161ff" />
        </div>
      )}
      </div>
    </div>
  );
};

export default ImageCropper;
